import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { RefreshCw, ChevronDown, ChevronUp, CheckCircle, Loader2 } from 'lucide-react';
import { api, unitNomineeService } from '../../../services/api';
import { API_ENDPOINTS } from '../../../config/api';
import { PendingOrder, transferModeLabel } from './paymentFormUtils';
import { usePermissions } from '../../../hooks/usePermissions';
import MaskedMobile from '../../common/MaskedMobile';
import { formatRawDateTime } from '../../../utils/format';
import { OrderUnit } from '../../../types';

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: any) => {
    if (n === null || n === undefined || isNaN(Number(n))) return '₹0';
    return `₹${Number(n).toLocaleString('en-IN')}`;
};
const methodLabel = (m: string) => m.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ── Order Card ───────────────────────────────────────────────────────────────

interface OrderCardProps {
    order: PendingOrder;
    userMobile: string;
    userId?: string;
    userName: string;
    userCountryCode?: string;
}

const OrderCard: React.FC<OrderCardProps> = ({ order, userMobile, userId, userName, userCountryCode }) => {
    const navigate = useNavigate();
    const canUpdateOrder = usePermissions().canAction('users', 'update_order', true);
    const [txOpen, setTxOpen] = useState(false);
    const [transactions, setTransactions] = useState<any[]>([]);
    const [loadingTx, setLoadingTx] = useState(false);
    const [paid, setPaid] = useState(order.paidAmount ?? 0);
    const [remaining, setRemaining] = useState(order.pendingAmount ?? order.totalCost ?? 0);

    const [nomineesOpen, setNomineesOpen] = useState(false);
    const [units, setUnits] = useState<OrderUnit[]>([]);
    const [loadingUnits, setLoadingUnits] = useState(false);

    const loadUnits = useCallback(async () => {
        setLoadingUnits(true);
        try {
            const result = await unitNomineeService.getOrderUnits(order.id);
            setUnits(result.data?.units || []);
        } finally {
            setLoadingUnits(false);
        }
    }, [order.id]);

    // Loaded up front (not lazily on toggle) so the footer's "Add Nominee" shortcut knows
    // right away whether any unit still needs one.
    useEffect(() => { loadUnits(); }, [loadUnits]);

    const handleToggleNominees = () => setNomineesOpen(v => !v);


    const loadTransactions = useCallback(async () => {
        setLoadingTx(true);
        try {
            const res = await api.get<any>(API_ENDPOINTS.getOrderDetails(order.id));
            if (res?.data?.status === 'success') {
                const txns: any[] = res.data.transactions || [];
                const orderData = res.data.order || {};
                const total = orderData.totalCost ?? order.totalCost ?? 0;
                const paidSum = txns.reduce((s: number, t: any) => s + (Number(t.amount) || 0), 0);
                setTransactions(txns);
                setPaid(paidSum);
                setRemaining(Math.max(total - paidSum, 0));
            }
        } catch {
            // keep existing values
        } finally {
            setLoadingTx(false);
        }
    }, [order.id, order.totalCost]);

    const handleToggleTx = () => {
        if (!txOpen && transactions.length === 0) loadTransactions();
        setTxOpen(v => !v);
    };

    const pct = order.totalCost ? Math.round((paid / order.totalCost) * 100) : 0;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">

            {/* ── Header ── */}
            <div className="px-6 py-4 grid grid-cols-3 gap-3 border-b border-slate-100">
                <div className="min-w-0">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Order ID</p>
                    <p className="text-xs font-bold text-slate-800 truncate">{order.id}</p>
                    {order.placedAt && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                            {formatRawDateTime(order.placedAt)}
                        </p>
                    )}
                </div>
                <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Units</p>
                    <p className="text-xs font-bold text-slate-800">{order.numUnits || 1} Unit{(order.numUnits || 1) > 1 ? 's' : ''}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{order.buffaloCount || 0} buffaloes · {order.calfCount || 0} calves</p>
                </div>
                <div className="flex flex-col items-end">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Status</p>
                    <span className="px-3 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">Pending Pay</span>
                </div>
            </div>

            {/* ── Amounts + Progress ── */}
            <div className="px-6 py-4 grid grid-cols-4 gap-4 border-b border-slate-100">
                <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Total</p>
                    <p className="text-base font-bold text-slate-800">{fmt(order.totalCost || 0)}</p>
                </div>
                <div>
                    <p className="text-[10px] font-semibold text-green-500 uppercase tracking-wide mb-1">Paid</p>
                    <p className="text-base font-bold text-green-600">{fmt(paid)}</p>
                </div>
                <div>
                    <p className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide mb-1">Remaining</p>
                    <p className="text-base font-bold text-amber-600">{fmt(remaining)}</p>
                </div>
                <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Progress</p>
                    <p className="text-base font-bold text-slate-700">{pct}%</p>
                    <div className="mt-2 w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                </div>
            </div>

            {/* ── Transactions toggle ── */}
            <button type="button" onClick={handleToggleTx}
                className="w-full px-6 py-3 flex items-center justify-between text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors border-b border-slate-100">
                <span>Transactions</span>
                {loadingTx
                    ? <Loader2 size={13} className="animate-spin text-slate-400" />
                    : txOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {txOpen && (
                <div className="px-6 py-4 border-b border-slate-100 space-y-2">
                    {loadingTx && <p className="text-xs text-slate-400">Loading transactions…</p>}
                    {!loadingTx && transactions.length === 0 && <p className="text-xs text-slate-400">No transactions yet.</p>}
                    {!loadingTx && transactions.map((tx: any, i: number) => (
                        <div key={tx.id || i} className="flex items-start justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
                            <div className="space-y-0.5">
                                <div className="flex items-center gap-1.5">
                                    <CheckCircle size={12} className="text-green-500 shrink-0" />
                                    <span className="text-xs font-semibold text-slate-700">{methodLabel(tx.paymentMethod || tx.paymentType || '—')}</span>
                                    {tx.transferMode && <span className="text-[10px] text-slate-400">({transferModeLabel(tx.transferMode)})</span>}
                                </div>
                                {tx.transactionDate && <p className="text-[10px] text-slate-400 ml-4">{formatRawDateTime(tx.transactionDate)}</p>}
                                {tx.utrNumber && <p className="text-[10px] text-slate-500 ml-4">UTR: {tx.utrNumber}</p>}
                            </div>
                            <p className="text-sm font-bold text-green-700 shrink-0 ml-4">{fmt(Number(tx.amount) || 0)}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Nominees toggle ── */}
            <button type="button" onClick={handleToggleNominees}
                className="w-full px-6 py-3 flex items-center justify-between text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors border-b border-slate-100">
                <span>Nominees</span>
                {loadingUnits
                    ? <Loader2 size={13} className="animate-spin text-slate-400" />
                    : nomineesOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {nomineesOpen && (
                <div className="px-6 py-4 border-b border-slate-100 space-y-2">
                    {loadingUnits && <p className="text-xs text-slate-400">Loading units…</p>}
                    {!loadingUnits && units.length === 0 && <p className="text-xs text-slate-400">No units found for this order.</p>}
                    {!loadingUnits && units.map(u => (
                        <div key={u.unit_id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
                            <div>
                                <p className="text-[10px] text-slate-400 font-mono">{u.unit_id}</p>
                                {u.nominee ? (
                                    <p className="text-xs font-semibold text-slate-700 mt-0.5">{u.nominee.nominee_full_name} ({u.nominee.relation})</p>
                                ) : (
                                    <p className="text-xs text-slate-400 mt-0.5">No nominee added</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Footer ── */}
            <div className="px-6 py-3.5 flex items-center justify-between bg-slate-50/60">
                <span className="text-[11px] text-slate-400 font-medium">
                    {paid > 0 ? 'Partial payment received' : 'No payment recorded yet'}
                </span>
                <div className="flex items-center gap-2">
                    {canUpdateOrder && (
                        <button
                            onClick={() => navigate(`/users/customers/${userMobile}/orders/${encodeURIComponent(order.id)}/add-payment`, {
                                state: { fromPage: 'Pending Orders', userName, countryCode: userCountryCode, userId, orderTotal: order.totalCost, numUnits: order.numUnits },
                            })}
                            className="px-4 py-2 bg-[#1B4332] hover:bg-[#163829] text-white text-xs font-bold rounded-xl shadow-sm active:scale-95 transition-all">
                            Update Payment
                        </button>
                    )}
                </div>
            </div>

        </div>
    );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const UserPendingOrdersPage: React.FC = () => {
    const { mobile } = useParams<{ mobile: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const [orders, setOrders] = useState<PendingOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const userName: string = (location.state as any)?.userName || '';
    const userCountryCode: string | undefined = (location.state as any)?.countryCode;
    const userId: string | undefined = (location.state as any)?.userId;

    const fetchOrders = useCallback(async () => {
        if (!mobile) return;
        setLoading(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getUserOrders(mobile), {
                params: { paymentStatus: 'PENDING_PAYMENT' },
            });
            setOrders(response.data?.orders || response.data || []);
        } catch {
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [mobile]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    return (
        <div className="w-full pb-12 font-sans">

            {/* Page header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Pending Orders</h1>
                <nav className="flex items-center gap-1.5 text-sm">
                    <button onClick={() => navigate(-1)}
                        className="text-slate-500 hover:text-slate-800 font-medium transition-colors">
                        Users
                    </button>
                    <span className="text-slate-300 select-none">›</span>
                    <span className="text-slate-800 font-semibold">Pending Orders</span>
                </nav>
            </div>

            {/* User strip */}
            <div className="bg-white rounded-2xl border border-slate-200 px-6 py-4 flex items-center justify-between mb-6">
                <div>
                    {userName && <p className="text-sm font-bold text-slate-800">{userName}</p>}
                    <MaskedMobile mobile={mobile} countryCode={userCountryCode} className="text-xs text-slate-400 font-medium mt-0.5" />
                </div>
                <button onClick={fetchOrders}
                    className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Refresh">
                    <RefreshCw className="w-4 h-4" />
                </button>
            </div>

            {/* Loading skeleton */}
            {loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden animate-pulse">
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between">
                                <div className="space-y-2">
                                    <div className="h-3.5 w-44 bg-slate-100 rounded" />
                                    <div className="h-3 w-28 bg-slate-100 rounded" />
                                </div>
                                <div className="h-6 w-24 bg-amber-100 rounded-full" />
                            </div>
                            <div className="px-6 py-4 grid grid-cols-3 gap-4">
                                {[1, 2, 3].map(j => <div key={j} className="h-10 bg-slate-100 rounded-xl" />)}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty */}
            {!loading && orders.length === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 py-20 text-center">
                    <p className="text-sm font-bold text-slate-600">No pending orders</p>
                    <p className="text-xs text-slate-400 mt-1">This user has no orders with pending payment.</p>
                </div>
            )}

            {/* Orders grid */}
            {!loading && orders.length > 0 && (
                <>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                        {orders.length} Order{orders.length !== 1 ? 's' : ''}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {orders.map(order => (
                            <OrderCard
                                key={order.id}
                                order={order}
                                userMobile={mobile || ''}
                                userId={userId}
                                userName={userName}
                                userCountryCode={userCountryCode}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default UserPendingOrdersPage;
